import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Handshake } from "lucide-react";
import { fetchResellerClients, type ResellerClientRegister } from "../services/api";
import { downloadCsv } from "../services/csv";
import { filterRows, monthChoices, registerCsv, saMonth, stateOf, type StatusFilter } from "../services/resellerClients";
import { usePageTitle } from "../hooks/usePageTitle";

/**
 * Every reseller's clients (tenant plan phase 21): live or paused, since
 * when, how many days of the chosen month each was live, and who uses it.
 * Orbit bills nobody automatically; this is the list resellers are invoiced
 * from by hand. A client a reseller let go during the month stays on that
 * month's list for the days it was live.
 */

const day = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" }) : "";

const PILL: Record<ReturnType<typeof stateOf>, string> = { Live: "success", Paused: "warn", "Let go": "" };

export function ResellerClientsPage() {
  usePageTitle("Reseller clients");
  const months = useMemo(() => monthChoices(), []);
  const [month, setMonth] = useState(saMonth());
  const [resellerId, setResellerId] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [data, setData] = useState<ResellerClientRegister | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchResellerClients(month)
      .then((d) => { if (active) setData(d); })
      .catch((e) => { if (active) setError(e instanceof Error ? e.message : "Could not load the client list."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [month]);

  const rows = useMemo(() => (data ? filterRows(data.items, resellerId, status) : []), [data, resellerId, status]);
  const monthName = months.find((m) => m.month === month)?.label ?? month;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Customers</span>
          <h1>Reseller clients</h1>
          <p className="muted">
            Every reseller's client workspaces: live or paused, since when, and the days each was live in a month. Resellers
            are invoiced by hand from this list; nothing here bills anyone.
          </p>
        </div>
      </header>

      <div className="card" style={{ padding: 16 }}>
        <div className="row wrap" style={{ gap: 12, alignItems: "flex-end" }}>
          <label style={{ flex: "0 1 200px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Month</span>
            <select value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}
            </select>
          </label>
          <label style={{ flex: "0 1 240px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Reseller</span>
            <select value={resellerId} onChange={(e) => setResellerId(e.target.value)}>
              <option value="">Every reseller</option>
              {(data?.resellers ?? []).map((r) => <option key={r.tenantId} value={r.tenantId}>{r.name}</option>)}
            </select>
          </label>
          <label style={{ flex: "0 1 220px" }}>
            <span className="muted" style={{ fontSize: 12 }}>Showing</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
              <option value="all">Every client</option>
              <option value="live_in_month">Live at some point in {monthName}</option>
              <option value="live">Live now</option>
              <option value="paused">Paused or let go</option>
            </select>
          </label>
          <button
            type="button"
            className="btn"
            disabled={!data || rows.length === 0}
            onClick={() => {
              if (!data) return;
              const f = registerCsv(data, rows);
              downloadCsv(f.filename, f.headers, f.rows);
            }}
          >
            <Download size={14} /> Download CSV
          </button>
        </div>
      </div>

      {data && data.resellers.length > 0 ? (
        <div className="table-wrap" aria-label="Per reseller">
          <table className="table">
            <thead>
              <tr>
                <th>Reseller</th>
                <th>Clients</th>
                <th>Live now</th>
                <th>Live in {monthName}</th>
                <th>Client-days live</th>
              </tr>
            </thead>
            <tbody>
              {data.resellers.map((r) => (
                <tr key={r.tenantId}>
                  <td><Link to={`/companies/${r.tenantId}`}>{r.name}</Link></td>
                  <td>{r.clients}</td>
                  <td>{r.liveNow}</td>
                  <td>{r.liveInMonth}</td>
                  <td>{r.clientDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="table-wrap" aria-label="Clients">
        {loading ? (
          <div className="empty-state"><p className="muted">Loading reseller clients…</p></div>
        ) : error ? (
          <div className="empty-state"><p className="muted">{error}</p></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Handshake size={28} />
            <h3>No clients</h3>
            <p>Clients appear here when a reseller adds one from its Clients page.</p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Reseller</th>
                <th>State</th>
                <th>Live since</th>
                <th>Set up</th>
                <th>First sign-in</th>
                <th>Days live</th>
                <th>People active</th>
                <th>Last activity</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const state = stateOf(r);
                return (
                  <tr key={`${r.resellerId}:${r.tenantId}`} aria-label={r.name}>
                    <td><Link to={`/companies/${r.tenantId}`}>{r.name}</Link></td>
                    <td>{r.resellerName}</td>
                    <td><span className={`pill ${PILL[state]}`}>{state}</span></td>
                    <td>{r.liveSince ? day(r.liveSince) : <span className="faint">{r.pausedSince ? `Not live since ${day(r.pausedSince)}` : ""}</span>}</td>
                    <td>{day(r.setUpAt)}</td>
                    <td>{r.firstSignInAt ? day(r.firstSignInAt) : <span className="faint">Nobody yet</span>}</td>
                    <td>{r.daysLive}</td>
                    <td>{r.people ? `${r.activePeople} of ${r.people}` : <span className="faint">Nobody yet</span>}</td>
                    <td>{r.lastActivityAt ? day(r.lastActivityAt) : <span className="faint">Never</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
